import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import axios from 'axios';

const ImageCacheContext = createContext();

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BATCH_CHUNK_SIZE = 25; // Images per API call — keeps payload < 12 MB even with 500 KB each
const BATCH_DELAY_MS = 60;   // Wait for more requests before firing
const MAX_RETRIES = 2;

// Direct-URL fallback: if batch fails or image missing from response, use the
// single-image GET endpoint. Browser will cache it for 1 year (immutable).
const directImageUrl = (filename) => `${API}/images/part/${encodeURIComponent(filename)}`;

// In-memory cache for images (persists across re-renders)
const imageCache = new Map();

// Track failed images to avoid infinite retry loops
const failedImages = new Map(); // filename -> retryCount

// Pending requests to avoid duplicate fetches
const pendingRequests = new Map();

export const useImageCache = () => {
  const context = useContext(ImageCacheContext);
  if (!context) {
    throw new Error('useImageCache must be used within ImageCacheProvider');
  }
  return context;
};

export const ImageCacheProvider = ({ children }) => {
  const [, forceUpdate] = useState({});
  const batchQueueRef = useRef([]);
  const batchTimeoutRef = useRef(null);
  const processingRef = useRef(false);

  const getCachedImage = useCallback((filename) => {
    if (!filename) return null;
    return imageCache.get(filename) || null;
  }, []);

  const isImageCached = useCallback((filename) => {
    if (!filename) return false;
    return imageCache.has(filename);
  }, []);

  // Send a single chunk of filenames to the batch API
  const fetchChunk = async (chunkItems) => {
    const filenames = chunkItems.map(item => item.filename);
    try {
      const response = await axios.post(`${API}/images/batch`, filenames, { timeout: 30000 });
      const images = response.data;

      chunkItems.forEach(({ filename, resolve }) => {
        // If batch returned data, use it; else fall back to direct URL (browser
        // fetches + caches for 1 year via Cache-Control immutable).
        const imageData = images[filename] || directImageUrl(filename);
        imageCache.set(filename, imageData);
        failedImages.delete(filename);
        pendingRequests.delete(filename);
        resolve(imageData);
      });
    } catch (error) {
      console.warn('Batch image chunk failed — falling back to direct URLs:', error.message);
      // Batch failed (timeout / 500 / network) → use direct URLs so images still render
      chunkItems.forEach(({ filename, resolve }) => {
        const url = directImageUrl(filename);
        imageCache.set(filename, url);
        pendingRequests.delete(filename);
        resolve(url);
      });
    }
  };

  // Process the full batch queue — splits into chunks of BATCH_CHUNK_SIZE
  const processBatch = async () => {
    if (processingRef.current) return;
    processingRef.current = true;

    const batch = [...batchQueueRef.current];
    batchQueueRef.current = [];

    if (batch.length === 0) {
      processingRef.current = false;
      return;
    }

    // Split into chunks and send concurrently (max 3 concurrent)
    const chunks = [];
    for (let i = 0; i < batch.length; i += BATCH_CHUNK_SIZE) {
      chunks.push(batch.slice(i, i + BATCH_CHUNK_SIZE));
    }

    // Process chunks with concurrency limit of 6 (browsers support up to 6
    // concurrent requests per origin — maximise throughput without blocking)
    const concurrency = 6;
    for (let i = 0; i < chunks.length; i += concurrency) {
      const concurrent = chunks.slice(i, i + concurrency);
      await Promise.all(concurrent.map(chunk => fetchChunk(chunk)));
    }

    processingRef.current = false;
    forceUpdate({});

    // If more items queued during processing, process them
    if (batchQueueRef.current.length > 0) {
      processBatch();
    }
  };

  // Queue an image for batch fetching
  const fetchImage = useCallback((filename) => {
    if (!filename) return Promise.resolve(null);

    if (imageCache.has(filename)) {
      return Promise.resolve(imageCache.get(filename));
    }

    if (pendingRequests.has(filename)) {
      return pendingRequests.get(filename);
    }

    // Skip if permanently failed
    if ((failedImages.get(filename) || 0) > MAX_RETRIES) {
      return Promise.resolve(null);
    }

    const promise = new Promise((resolve) => {
      batchQueueRef.current.push({ filename, resolve });

      if (batchTimeoutRef.current) {
        clearTimeout(batchTimeoutRef.current);
      }

      batchTimeoutRef.current = setTimeout(() => {
        processBatch();
      }, BATCH_DELAY_MS);
    });

    pendingRequests.set(filename, promise);
    return promise;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const prefetchImages = useCallback((filenames) => {
    const uncached = filenames.filter(f => f && !imageCache.has(f) && !pendingRequests.has(f) && (failedImages.get(f) || 0) <= MAX_RETRIES);
    if (uncached.length > 0) {
      uncached.forEach(filename => fetchImage(filename));
    }
  }, [fetchImage]);

  const clearCache = useCallback((filename) => {
    if (filename) {
      imageCache.delete(filename);
      failedImages.delete(filename);
    } else {
      imageCache.clear();
      failedImages.clear();
    }
    forceUpdate({});
  }, []);

  const value = {
    getCachedImage,
    isImageCached,
    fetchImage,
    prefetchImages,
    clearCache
  };

  return (
    <ImageCacheContext.Provider value={value}>
      {children}
    </ImageCacheContext.Provider>
  );
};

export default ImageCacheContext;
